"use client";

import { useState } from "react";
import { ChevronDown, Plus, Minus } from "lucide-react";
import { InputConfig, NumberInput, SelectInput, RadioInput, ToggleInput } from "@/lib/tools/types";

interface CalcInputProps {
  input: InputConfig;
  value: string | number | boolean;
  onChange: (value: string | number | boolean) => void;
}

/**
 * Renders a single input field based on InputConfig type.
 * Supports: number (with range slider), select (with search), radio, toggle
 */
export function CalcInput({ input, value, onChange }: CalcInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  if (input.type === "number") {
    const numInput = input as NumberInput;
    const numValue = typeof value === "number" ? value : numInput.default;

    return (
      <div className="space-y-3">
        <label className="block text-sm font-semibold text-foreground">
          {numInput.label}
        </label>
        {numInput.helpText && (
          <p className="text-xs text-muted-foreground">{numInput.helpText}</p>
        )}

        {/* Number Input */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onChange(Math.max(numInput.min, numValue - numInput.step))}
            className="p-2 rounded-md border border-border hover:bg-secondary transition-colors"
          >
            <Minus className="h-4 w-4" />
          </button>
          <input
            type="number"
            value={numValue}
            onChange={(e) => onChange(Math.min(numInput.max, Math.max(numInput.min, parseFloat(e.target.value) || numInput.min)))}
            className="flex-1 px-3 py-2 rounded-md border border-border bg-background focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
            min={numInput.min}
            max={numInput.max}
            step={numInput.step}
          />
          <button
            onClick={() => onChange(Math.min(numInput.max, numValue + numInput.step))}
            className="p-2 rounded-md border border-border hover:bg-secondary transition-colors"
          >
            <Plus className="h-4 w-4" />
          </button>
          {numInput.suffix && (
            <span className="text-sm font-medium text-muted-foreground ml-1">{numInput.suffix}</span>
          )}
        </div>

        {/* Range Slider */}
        <input
          type="range"
          min={numInput.min}
          max={numInput.max}
          step={numInput.step}
          value={numValue}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-brand"
        />

        {/* Display formatted value */}
        <div className="text-right text-xs text-muted-foreground">
          {numInput.prefix || ""}{numValue}{numInput.suffix || ""}
        </div>
      </div>
    );
  }

  if (input.type === "select") {
    const selInput = input as SelectInput;
    const selectValue = String(value);
    const selectedLabel =
      selInput.options.find((o) => o.value === selectValue)?.label || selInput.options[0]?.label;
    const filteredOptions = selInput.options.filter((o) =>
      o.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="space-y-3">
        <label className="block text-sm font-semibold text-foreground">
          {selInput.label}
        </label>
        {selInput.helpText && (
          <p className="text-xs text-muted-foreground">{selInput.helpText}</p>
        )}

        {/* Dropdown */}
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full px-3 py-2 rounded-md border border-border bg-background text-left text-sm flex items-center justify-between hover:border-border/70 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
          >
            <span>{selectedLabel}</span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </button>

          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 z-10 rounded-md border border-border bg-background shadow-lg">
              {/* Search */}
              {selInput.options.length > 8 && (
                <div className="border-b border-border p-2">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-2 py-1 rounded text-sm border border-border bg-background focus:ring-2 focus:ring-brand outline-none"
                  />
                </div>
              )}

              {/* Options */}
              <div className="max-h-48 overflow-y-auto">
                {filteredOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                      setSearchTerm("");
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors ${
                      selectValue === opt.value ? "bg-brand/10 text-brand font-medium" : ""
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (input.type === "radio") {
    const radInput = input as RadioInput;
    const radValue = String(value);

    return (
      <div className="space-y-3">
        <label className="block text-sm font-semibold text-foreground">
          {radInput.label}
        </label>

        {/* Radio buttons - horizontal */}
        <div className="flex flex-wrap gap-2">
          {radInput.options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-all ${
                radValue === opt.value
                  ? "bg-brand border-brand text-brand-foreground"
                  : "border-border text-foreground hover:border-brand/50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (input.type === "toggle") {
    const togInput = input as ToggleInput;
    const togValue = Boolean(value);

    return (
      <div className="space-y-3">
        <label className="block text-sm font-semibold text-foreground">
          {togInput.label}
        </label>
        {togInput.helpText && (
          <p className="text-xs text-muted-foreground">{togInput.helpText}</p>
        )}

        {/* Toggle switch */}
        <button
          onClick={() => onChange(!togValue)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            togValue ? "bg-brand" : "bg-secondary"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${
              togValue ? "translate-x-5.5" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    );
  }

  return null;
}
